import React, {useContext, useEffect, useRef, useState} from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Fade,
  FormControlLabel,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import {Link} from "react-router-dom";
import {ArrowForwardIos, ArrowBackIosNew} from "@mui/icons-material";
import {toast} from "react-toastify";
import {SignInContext} from "../../../context/SignInContext";
import {IconBrandGoogle} from "@tabler/icons-react";
import {OpenbrainLogoLink} from "../../../components/links/OpenbrainLogoLink";
import {FitrLogoLink} from "../../../components/links/FitrLogoLink";
import {mapToUrl} from "../../../utils/functions";
import {c, mono, softShadow, primaryButtonSx} from "../../landing/landingTokens";
import {LiquidEther} from "../../../components/voice-orb";

// Soft, borderless filled fields — rounded, faint fill, no underline. On focus the fill
// warms to a light purple and a gentle glow ring replaces any hard border.
const fieldSx = {
  "& .MuiFilledInput-root": {
    borderRadius: "12px",
    backgroundColor: "rgba(20,21,26,0.04)",
    overflow: "hidden",
    transition: "background-color .2s ease, box-shadow .2s ease",
    "&:before, &:after": {display: "none"},
    "&:hover": {backgroundColor: "rgba(20,21,26,0.06)"},
    "&.Mui-focused": {
      backgroundColor: "rgba(129,140,248,0.08)",
      boxShadow: "0 0 0 2px rgba(129,140,248,0.45)",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {color: c.purpleDeep},
};

export const LoginPage = () => {
  const [loginWithMail, setLoginWithMail] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const passwordRef = useRef(null);
  const {onLogin} = useContext(SignInContext);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  useEffect(() => {
    if (loginWithMail) {
      passwordRef.current?.focus();
    }
  }, [loginWithMail])


  const loginHandle = async (e) => {
    e.preventDefault();
    if (!loginWithMail) {
      setLoginWithMail(true);
      return;
    }
    try {
      setLoading(true);
      await onLogin({username: email, password: password});
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{
      position: "relative", width: "100%", minHeight: "100%",
      bgcolor: c.bg, display: "flex", flexDirection: "column", overflow: "hidden"
    }}>
      {/* Mouse-reactive fluid backdrop (React Bits LiquidEther) — matches the landing hero,
          toned down for an auth page. autoDemo is disabled when the user prefers reduced motion. */}
      <Box aria-hidden sx={{position: "absolute", inset: 0, zIndex: 0, opacity: 0.3, pointerEvents: "none"}}>
        <LiquidEther
          colors={["#8B7CFF", "#E2A6FF", "#C7D2FE"]}
          mouseForce={12}
          cursorSize={90}
          resolution={0.5}
          autoDemo={!reduceMotion}
          autoSpeed={0.3}
          autoIntensity={1.0}
          autoResumeDelay={800}
          autoRampDuration={1}
        />
      </Box>

      <Stack sx={{position: "relative", zIndex: 1, flex: 1, minHeight: "100%"}}>
        {/* TOP BAR — logo + back-to-marketing link */}
        <Toolbar sx={{gap: 1.75}}>
          <img src={"/logo.svg"} alt={"logo"} width={30}/>
          <Box
              component={Link}
              to="/"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontFamily: mono,
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: c.purpleDeep,
                textDecoration: "none",
                opacity: 0.9,
                "&:hover": {opacity: 1},
              }}
          >
            <ArrowBackIosNew sx={{fontSize: 10}}/> openbrain.io
          </Box>
        </Toolbar>

        {/* CARD */}
        <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 2, py: 3}}>
          <Stack
            direction={"column"} gap={3}
            sx={{
              maxWidth: 420, width: "100%", boxSizing: "border-box",
              px: {xs: 3, sm: 4.5}, py: {xs: 4, sm: 5},
              borderRadius: "22px", bgcolor: c.white, boxShadow: softShadow, mx: "auto"
            }}
            justifyContent={'center'} alignItems={"center"}
          >
            {/*HEADER*/}
            <Stack direction={"column"} gap={1.25} sx={{width: '100%'}}>
              <Typography sx={{
                fontFamily: mono, fontSize: 11.5, letterSpacing: "0.16em",
                textTransform: "uppercase", color: c.purpleDeep, textAlign: "center"
              }}>
                OpenBrain
              </Typography>
              {/*TITLE*/}
              <Typography sx={{
                fontSize: {xs: 34, sm: 42}, fontWeight: 700, letterSpacing: "-0.03em",
                color: c.ink, textAlign: "center", lineHeight: 1.05
              }}>
                Sign in
              </Typography>
              {/*SIGN UP*/}
              <Box sx={{textAlign: "center"}}>
                <Typography sx={{color: c.inkSoft, fontSize: 14.5}} component={"span"}>
                  Don't have an account yet?
                </Typography>
                <Link to={`/register${window.location.search}`} style={{
                  paddingLeft: 5,
                  color: c.purpleDeep,
                  fontWeight: 600,
                  textDecoration: "none",
                  cursor: "pointer"
                }}>
                  Sign up
                </Link>
              </Box>
            </Stack>
            {/*LOGIN WITH MAIL*/}
            <Stack component={"form"} onSubmit={loginHandle} direction={"column"} gap={1.5} sx={{width: '100%'}}>
              <TextField
                variant={"filled"}
                required
                fullWidth
                label={"Email"}
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={"Enter your email address"}
                sx={fieldSx}
              />
              {
                loginWithMail &&
                <Fade
                  in={loginWithMail}
                >
                  <TextField
                    ref={passwordRef}
                    autoFocus
                    type={"password"}
                    variant={"filled"}
                    required
                    fullWidth
                    label={"Password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={"Password"}
                    sx={fieldSx}
                  />
                </Fade>
              }

              <Stack direction={"row"} gap={2} justifyContent={"space-between"} alignItems={"center"}
                     useFlexGap flexWrap={"wrap"}>
                <FormControlLabel sx={{color: c.inkSoft}} control={<Checkbox defaultChecked
                                                                             sx={{"&.Mui-checked": {color: c.purpleDeep}}}/>}
                                  label="Remember me"/>
                <Typography sx={{textDecoration: "none", color: c.inkSoft, fontSize: 14}} component={Link}
                            to={"/forgot-password"}>Forgot password?</Typography>
              </Stack>

              <Button
                fullWidth
                type={"submit"}
                disabled={loading || (!loginWithMail && !email)}
                sx={{...primaryButtonSx, py: 1.4, fontSize: 15}}
                endIcon={loading
                  ? <CircularProgress size={16} color={"inherit"}/>
                  : <ArrowForwardIos style={{fontSize: 13}}/>}
              >
                {loginWithMail ? "Log in" : "Continue"}
              </Button>

              <Divider sx={{
                color: c.inkMuted, fontFamily: mono, fontSize: 11, letterSpacing: "0.14em",
                "&::before, &::after": {borderColor: c.line}
              }}>OR</Divider>
              <Button
                component={'a'}
                href={mapToUrl(`/oauth2/authorization/google?redirect_uri=${process.env.REACT_APP_GATEWAY}/oauth2/redirect`, process.env.REACT_APP_USERMANAGEMENT_GATEWAY)}
                fullWidth
                startIcon={<IconBrandGoogle/>}
                sx={{
                  borderRadius: "980px", textTransform: "none", fontWeight: 500, fontSize: 14,
                  py: 1.25, color: c.ink, border: `1px solid ${c.lineStrong}`,
                  "&:hover": {borderColor: c.purpleSoft, bgcolor: "rgba(129,140,248,0.06)"}
                }}
              >
                Google
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* FOOTER */}
        <Toolbar sx={{justifyContent: "space-between"}}>
          <OpenbrainLogoLink/>
          <FitrLogoLink/>
        </Toolbar>
      </Stack>
    </Box>
  )
}
